import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTrip } from "@/hooks/useTrip";
import { TripComponent, type ComponentType } from "@shared/schema";
import ItineraryItem from "@/components/ItineraryItem";
import TripFormDialog from "@/components/TripFormDialog";
import TripAssistantColumn from "@/components/TripAssistantColumn";
import { PdfImportDialog } from "@/components/PdfImportDialog";
import { CommandBar, TraditionalSidebar } from "@/pages/CanvasUI";
import { useAlphaFeatureGates } from "@/hooks/useAlphaFeatureGates";
import { useCommandBarSearchData } from "@/hooks/useCommandBarSearchData";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import QuickAddModal from "@/components/QuickAddModal";
import AddComponentModal from "@/components/AddComponentModal";
import {
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Sparkles as SparklesIcon,
  X as XIcon,
  Filter,
  Globe,
  MapPin as MapPinIcon,
  MessageSquare,
  MoreVertical,
  Plus as PlusIcon,
  Settings,
  Sparkles,
  Upload,
  Users,
  GripVertical
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GlobalHeader, GLOBAL_HEADER_HEIGHT, BELOW_CHROME_HEIGHT } from "@/components/GlobalHeader";

type ViewType = "timeline" | "daily" | "list";

// Remove local ThreadsSidebar in favor of shared TraditionalSidebar

export default function TripPlannerCanvas() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const tripId = parseInt(id);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Alpha feature gates for non-admin users
  const { gates: alphaGates } = useAlphaFeatureGates();

  const [collapsed, setCollapsed] = useState(false);
  const [viewType, setViewType] = useState<ViewType>("timeline");
  const [isAssistantOpen, setIsAssistantOpen] = useState(true);
  
  // Shared search data for CommandBar context cards
  const { contactsData, tripsData, searchDataLoaded } = useCommandBarSearchData();
  const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false);
  const [isPdfImportOpen, setIsPdfImportOpen] = useState(false);
  const [hideMeals, setHideMeals] = useState(false);
  const [visibleDayRange, setVisibleDayRange] = useState<[number, number]>([0, 2]);
  
  // Reset visible day range when switching to timeline view
  useEffect(() => {
    if (viewType === 'timeline') {
      // Always reset to show from the beginning when switching to timeline view
      setVisibleDayRange([0, 2]);
    }
  }, [viewType]);
  
  // Quick add modals
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [isAddComponentModalOpen, setIsAddComponentModalOpen] = useState(false);
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentType | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | undefined>();

  // Drag and drop state
  const [activeDayId, setActiveDayId] = useState<string | null>(null);
  const [dayOrder, setDayOrder] = useState<number[]>([]);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [contentHeightPx, setContentHeightPx] = useState<number>(480);
  const globalHeaderOffset = GLOBAL_HEADER_HEIGHT;
  const DAY_CARD_BODY_HEIGHT = 620; // increased by ~100px over previous size
  const DAY_CARD_HEADER_HEIGHT = 56; // approx header height (py-4)
  const DAY_CARD_TOTAL_HEIGHT = DAY_CARD_BODY_HEIGHT + DAY_CARD_HEADER_HEIGHT;

  const { trip, components = [], isLoading, groupedByDay, dateRange, dayCount, dayLocations } = useTrip(tripId);

  // Initialize day order when trip loads
  useEffect(() => {
    if (trip && dayCount > 0) {
      setDayOrder(Array.from({ length: dayCount }, (_, i) => i));
    }
  }, [dayCount, trip]);

  // Activity Stream (local slideout matching /threads)
  type ActivityItem = { id: string; type: 'approval' | 'suggestion' | 'complete' | 'background'; title: string; description: string; timestamp: Date; actions?: { label: string; type: 'primary' | 'secondary' }[] };
  const [activityStreamOpen, setActivityStreamOpen] = useState(false);
  const [activities] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const open = () => setActivityStreamOpen(true);
    window.addEventListener('openActivityStream', open as EventListener);
    return () => window.removeEventListener('openActivityStream', open as EventListener);
  }, []);

  // Command bar handlers (minimal wiring for now)
  const [isProcessing, setIsProcessing] = useState(false);
  const handleCommandSubmit = async (text: string) => {
    if (!text.trim()) return;
    try {
      setIsProcessing(true);
      // Route messages to the Trip Assistant column via event
      window.dispatchEvent(new CustomEvent('tripAssistantMessage', { detail: { tripId, text } }));
    } finally {
      setIsProcessing(false);
    }
  };

  const getCommandBarHeight = () => {
    // Prefer CSS variable provided by CommandBar (includes its bottom offset)
    const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--command-bar-height');
    const parsed = parseInt(cssVar.trim().replace('px', ''), 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    // Fallback to direct measurement + known bottom offset
    const el = document.querySelector('[data-command-bar="true"]') as HTMLElement | null;
    const bottomOffset = 20; // matches bottom-[20px]
    const h = el?.offsetHeight ?? 0;
    return (h > 0 ? h + bottomOffset : 160);
  };

  const getNoticeBannerHeight = () => {
    // NoticeBanner publishes its rendered height here while a notice is active.
    // It sits between the global header and this canvas, so its height eats into
    // the same viewport budget — without subtracting it the content box runs
    // past the bottom of the window and clips the command bar.
    const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--notice-banner-height');
    const parsed = parseFloat(cssVar.trim().replace('px', ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const recalcContentHeight = () => {
    const headerHeight = headerRef.current?.offsetHeight ?? 0; // includes header + sub-header
    const commandBarHeight = getCommandBarHeight();
    const noticeBannerHeight = getNoticeBannerHeight();
    const reservedMarginAboveBar = 0; // already included in CSS var
    const next = Math.max(
      window.innerHeight - globalHeaderOffset - noticeBannerHeight - headerHeight - commandBarHeight - reservedMarginAboveBar,
      240,
    );
    setContentHeightPx(next);
  };

  // Recompute on mount, on resize, when command bar or header size changes, and after mount retries
  useEffect(() => {
    let cancelled = false;
    const handleResize = () => !cancelled && recalcContentHeight();
    window.addEventListener('resize', handleResize);

    // Observe command bar size
    const barEl = document.querySelector('[data-command-bar="true"]') as HTMLElement | null;
    let ro: ResizeObserver | null = null;
    if (barEl && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => !cancelled && recalcContentHeight());
      ro.observe(barEl);
    }

    // Observe header size
    let headerRO: ResizeObserver | null = null;
    if (headerRef.current && 'ResizeObserver' in window) {
      headerRO = new ResizeObserver(() => !cancelled && recalcContentHeight());
      headerRO.observe(headerRef.current);
    }

    // Observe the notice banner — it can mount or reflow after this canvas.
    let noticeRO: ResizeObserver | null = null;
    const noticeEl = document.querySelector('[data-testid="notice-banner"]') as HTMLElement | null;
    if (noticeEl && 'ResizeObserver' in window) {
      noticeRO = new ResizeObserver(() => !cancelled && recalcContentHeight());
      noticeRO.observe(noticeEl);
    }

    // Retry until command bar mounts with non-zero height
    let attempts = 0;
    const tryRecalc = () => {
      if (cancelled) return;
      recalcContentHeight();
      const h = getCommandBarHeight();
      if (h <= 1 && attempts < 10) {
        attempts += 1;
        setTimeout(tryRecalc, 150);
      }
    };

    tryRecalc();

    return () => {
      cancelled = true;
      window.removeEventListener('resize', handleResize);
      if (ro) ro.disconnect();
      if (headerRO) headerRO.disconnect();
      if (noticeRO) noticeRO.disconnect();
    };
  }, []);

  const filterComponents = (list: TripComponent[]) => {
    if (!hideMeals) return list;
    const lowered = (s: string | null) => (s || '').toLowerCase();
    return list.filter(c => c.type !== 'food' && !(lowered(c.title).includes('breakfast') || lowered(c.title).includes('lunch') || lowered(c.title).includes('dinner') || lowered(c.title).includes('meal')));
  };

  const navigateDateRange = (direction: 'prev' | 'next') => {
    const [start, end] = visibleDayRange;
    if (direction === 'prev') {
      if (start > 0) setVisibleDayRange([start - 1, end - 1]);
    } else {
      if (start + 1 < dayCount) setVisibleDayRange([start + 1, end + 1]);
    }
  };

  const handleQuickAdd = (dayIndex?: number) => {
    setSelectedDayIndex(dayIndex);
    setIsQuickAddModalOpen(true);
  };

  const handleSelectComponentType = (type: ComponentType) => {
    setSelectedComponentType(type);
    setIsAddComponentModalOpen(true);
  };

  const handleComponentCreated = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
    setIsAddComponentModalOpen(false);
    setSelectedComponentType(null);
    setSelectedDayIndex(undefined);
    toast({ title: "Component Added", description: "Component created successfully" });
  };

  const handleDeleteComponent = async (id: number) => {
    try {
      const res = await fetch(`/api/components/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed');
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      toast({ title: 'Deleted', description: 'Component deleted successfully' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete component', variant: 'destructive' });
    }
  };

  const handleAddDay = async (position: 'before' | 'after') => {
    try {
      if (!trip) {
        toast({ title: 'Trip not loaded', variant: 'destructive' });
        return;
      }
      if (!trip.startDate || !trip.endDate) {
        toast({
          title: 'Dates Required',
          description: 'Set trip start and end dates before adding days.',
          variant: 'destructive',
        });
        return;
      }
      const currentStart = new Date(trip.startDate);
      const currentEnd = new Date(trip.endDate);
      
      let newStartDate, newEndDate;
      if (position === 'before') {
        newStartDate = addDays(currentStart, -1);
        newEndDate = currentEnd;
      } else {
        newStartDate = currentStart;
        newEndDate = addDays(currentEnd, 1);
      }

      const response = await fetch(`/api/trips/${tripId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: newStartDate.toISOString(),
          endDate: newEndDate.toISOString(),
        }),
      });

      if (!response.ok) throw new Error('Failed to update trip');

      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      
      toast({ 
        title: "Day Added", 
        description: `Added day ${position} existing days` 
      });
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Failed to add day. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveDayId(String(active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveDayId(null);
      return;
    }

    const activeIndex = dayOrder.findIndex(dayIndex => `day-${dayIndex}` === active.id);
    const overIndex = dayOrder.findIndex(dayIndex => `day-${dayIndex}` === over.id);

    if (activeIndex !== -1 && overIndex !== -1) {
      const newOrder = arrayMove(dayOrder, activeIndex, overIndex);
      setDayOrder(newOrder);
      
      // Here you would typically update the backend with the new day order
      // For now, we'll just show a toast
      toast({ 
        title: "Day Reordered", 
        description: "Day order updated successfully" 
      });
    }

    setActiveDayId(null);
  };

  // Placeholder Day Card Component
  const PlaceholderDayCard = ({ position, dayNumber }: { position: 'before' | 'after', dayNumber: string }) => (
    <div 
      className="border-2 border-dashed border-gray-400 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center shadow-none" 
      style={{ 
        height: DAY_CARD_TOTAL_HEIGHT,
        width: '120px' // Fixed width for compact size
      }}
    >
      <button 
        onClick={() => handleAddDay(position)}
        className="flex flex-col items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors px-3"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-dashed border-gray-500 hover:border-gray-700 transition-colors">
          <PlusIcon className="w-6 h-6" />
        </div>
        <div className="text-sm font-medium whitespace-nowrap">Add Day</div>
      </button>
    </div>
  );

  // Placeholder Day Card for List View
  const PlaceholderDayCardList = ({ position, dayNumber }: { position: 'before' | 'after', dayNumber: string }) => (
    <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors py-8 flex items-center justify-center">
      <button 
        onClick={() => handleAddDay(position)}
        className="flex flex-col items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border-2 border-dashed border-gray-400 hover:border-gray-600 transition-colors">
          <PlusIcon className="w-6 h-6" />
        </div>
        <div className="text-sm font-medium">Add Day</div>
      </button>
    </div>
  );

  // Draggable Day Card Component
  const DraggableDayCard = ({ dayIndex, displayPosition, dayDate, components: comps, isDragDisabled = false }: {
    dayIndex: number;
    displayPosition: number;
    dayDate: Date;
    components: typeof components;
    isDragDisabled?: boolean;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ 
      id: `day-${dayIndex}`,
      disabled: isDragDisabled
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const filteredComps = filterComponents(comps || []);

    return (
      <div
        ref={setNodeRef}
        style={{ ...style, height: DAY_CARD_TOTAL_HEIGHT, minWidth: '225px', flexShrink: 0 }}
        className={`border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden flex flex-col bg-white dark:bg-gray-800 ${
          isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
        }`}
      >
        <div className="bg-orange-600 text-white px-3 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isDragDisabled && (
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab hover:cursor-grabbing p-1 rounded hover:bg-orange-700 transition-colors"
              >
                <GripVertical className="w-4 h-4" />
              </div>
            )}
            <div className="font-semibold">Day {displayPosition + 1}</div>
          </div>
          <div className="text-sm opacity-90">{format(dayDate, 'MMM d')}</div>
        </div>
        <div className="px-3 py-5 space-y-4 overflow-auto flex-1">
          {filteredComps.length > 0 ? (
            filteredComps
              .sort((a, b) => {
                if (!a.startTime) return 1;
                if (!b.startTime) return -1;
                return a.startTime.localeCompare(b.startTime);
              })
              .map((c) => (
                <ItineraryItem key={c.id} component={c} onClick={() => {}} onDelete={() => handleDeleteComponent(c.id)} onEdit={() => {}} onRegenerate={() => {}} />
              ))
          ) : (
            <div className="text-center py-6">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">No activities yet</div>
              <button
                onClick={() => handleQuickAdd(dayIndex)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 dark:border-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                title="Add component"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Draggable List Day Card Component (for list view)
  const DraggableListDayCard = ({ dayIndex, displayPosition, dayDate, components: comps }: {
    dayIndex: number;
    displayPosition: number;
    dayDate: Date;
    components: typeof components;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ 
      id: `day-${dayIndex}`
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const filteredComps = filterComponents(comps || []);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden bg-white dark:bg-gray-800 ${
          isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
        }`}
      >
        <div className="bg-orange-600 text-white px-3 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab hover:cursor-grabbing p-1 rounded hover:bg-orange-700 transition-colors"
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="font-semibold">Day {displayPosition + 1}</div>
          </div>
          <div className="text-sm opacity-90">{format(dayDate, 'EEE, MMM d')}</div>
        </div>
        <div className="px-3 py-5 space-y-4">
          {filteredComps.length > 0 ? (
            filteredComps
              .sort((a, b) => {
                if (!a.startTime) return 1;
                if (!b.startTime) return -1;
                return a.startTime.localeCompare(b.startTime);
              })
              .map((c) => (
                <ItineraryItem key={c.id} component={c} onClick={() => {}} onDelete={() => handleDeleteComponent(c.id)} onEdit={() => {}} onRegenerate={() => {}} />
              ))
          ) : (
            <div className="text-center py-6">
              <div className="text-sm text-gray-500 mb-3">No activities</div>
              <button 
                onClick={() => handleQuickAdd(dayIndex)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
                title="Add component"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading || !trip) {
    return (
      <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
        <GlobalHeader />
        <div className="flex flex-1 items-center justify-center text-gray-600">Loading trip…</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <GlobalHeader />
      <div
        className="flex flex-1 bg-white dark:bg-gray-900"
        style={{ minHeight: BELOW_CHROME_HEIGHT, height: BELOW_CHROME_HEIGHT }}
      >
      <TraditionalSidebar
        onNavigate={(canvas) => {
          // Map CanvasUI canvases to routes
          if (canvas === 'threads') navigate('/threads');
          else if (canvas === 'clients') navigate('/clients');
          else if (canvas === 'trips') navigate('/trips');
          else if (canvas === 'pipeline') navigate('/sales-pipeline');
          else if (canvas === 'proposals') navigate('/proposals');
          else if (canvas === 'dashboard') navigate('/');
          else navigate('/');
        }}
        expanded={!collapsed}
        onToggleExpanded={() => setCollapsed(!collapsed)}
        user={{ displayName: trip.clientName || 'User', email: 'user@example.com' }}
        userMenuOpen={false}
        setUserMenuOpen={() => {}}
        logout={() => {}}
        onThreadsMouseEnter={() => {}}
        onThreadsMouseLeave={() => {}}
        alphaGates={alphaGates}
      />
      <div className="flex-1 flex flex-col">
        {/* Combined Header */}
        <div ref={headerRef} className="sticky top-[var(--global-header-height,35px)] z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="px-6 py-3 flex items-center justify-between">
            {/* Trip Info - 40% */}
            <div className="min-w-0 flex-[0.4]">
              <div className="flex items-center gap-3 mb-1">
                <button 
                  onClick={() => navigate('/trips')}
                  className="text-sm text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-1"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Back to Trips
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setIsEditTripModalOpen(true)} className="text-left">
                  <h1 className="text-lg font-semibold truncate hover:underline">{trip.name}</h1>
                </button>
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <CalendarIcon className="w-4 h-4" /> 
                  {trip.startDate ? new Date(trip.startDate).toLocaleDateString() : "Dates TBD"}
                </div>
              </div>
            </div>

            {/* View Controls - 40% */}
            <div className="flex-[0.4] flex items-center justify-center">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <Button variant={viewType === 'timeline' ? 'default' : 'outline'} size="sm" onClick={() => setViewType('timeline')}>Timeline</Button>
                  <Button variant={viewType === 'daily' ? 'default' : 'outline'} size="sm" onClick={() => setViewType('daily')}>Daily</Button>
                  <Button variant={viewType === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewType('list')}>List</Button>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <Switch id="hide-meals" checked={hideMeals} onCheckedChange={setHideMeals} />
                  <Label htmlFor="hide-meals" className="text-sm whitespace-nowrap">Hide Meals</Label>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="flex items-center gap-1"
                  onClick={() => handleQuickAdd()}
                >
                  Quick Add <PlusIcon className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Activity Stream - 20% */}
            <div className="flex-[0.2] flex justify-end">
              <button
                onClick={() => setActivityStreamOpen(true)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative"
                title="Activity Stream"
              >
                <SparklesIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-hidden flex gap-4 p-4 pt-5`} style={{ height: contentHeightPx, paddingBottom: 'calc(var(--command-bar-height, 160px) - 10px)' }}>
          {/* Trip Assistant (left) */}
          <div className={`border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm bg-white dark:bg-gray-800 flex-shrink-0 ${isAssistantOpen ? 'w-[360px]' : 'w-[48px]'} transition-all flex flex-col`}>
            <div className="border-b border-gray-200 dark:border-gray-700 p-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                {isAssistantOpen && <span className="font-medium text-gray-900 dark:text-gray-100">Trip Assistant</span>}
              </div>
              <Button variant="ghost" size="icon" aria-label={isAssistantOpen ? 'Collapse' : 'Expand'} onClick={() => setIsAssistantOpen(!isAssistantOpen)}>
                {isAssistantOpen ? <ChevronRightIcon className="w-4 h-4 rotate-180" /> : <ChevronRightIcon className="w-4 h-4" />}
              </Button>
            </div>
            {isAssistantOpen && (
              <div className="flex-1 overflow-auto pb-4">
                <TripAssistantColumn
                  key={(trip as any)?.id ?? tripId}
                  hideInput
                  externalMessageEventName="tripAssistantMessage"
                  trip={trip as any}
                  components={components as any}
                  onSuccess={() =>
                    queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] })
                  }
                  onTripUpdated={() =>
                    queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] })
                  }
                />
              </div>
            )}
          </div>

          {/* Itinerary views (right) */}
          <div className="flex-1 overflow-auto h-full">
            {viewType === 'timeline' && (
              <DndContext 
                sensors={sensors} 
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="flex flex-wrap gap-6">
                  {/* Before placeholder - only show when viewing first day */}
                  {visibleDayRange[0] === 0 && (
                    <PlaceholderDayCard position="before" dayNumber="Day 0" />
                  )}
                  
                  <SortableContext 
                    items={dayOrder.slice(visibleDayRange[0], visibleDayRange[0] + Math.min(dayCount - visibleDayRange[0], 10)).map(dayIndex => `day-${dayIndex}`)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {/* Render actual trip days in the correct order */}
                    {dayOrder.slice(visibleDayRange[0], visibleDayRange[0] + Math.min(dayCount - visibleDayRange[0], 10)).map((dayIndex, positionIndex) => {
                      // Only render valid day indices that are within the trip
                      if (dayIndex < 0 || dayIndex >= dayCount) return null;
                      
                      const dayDate = addDays(new Date(dateRange[0]), dayIndex);
                      const comps = groupedByDay[dayIndex] || [];
                      const displayPosition = visibleDayRange[0] + positionIndex;
                      
                      return (
                        <DraggableDayCard
                          key={`day-${dayIndex}`}
                          dayIndex={dayIndex}
                          displayPosition={displayPosition}
                          dayDate={dayDate}
                          components={comps}
                        />
                      );
                    })}
                  </SortableContext>
                  
                  {/* After placeholder - only show when viewing last days */}
                  {visibleDayRange[0] + Math.min(dayCount - visibleDayRange[0], 10) >= dayCount && (
                    <PlaceholderDayCard position="after" dayNumber={`Day ${dayCount + 1}`} />
                  )}
                </div>
                
                <DragOverlay>
                  {activeDayId ? (
                    <div className="opacity-95">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden flex flex-col bg-white dark:bg-gray-800" style={{ height: DAY_CARD_TOTAL_HEIGHT, minWidth: '225px' }}>
                        <div className="bg-orange-600 text-white px-3 py-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4" />
                            <div className="font-semibold">Day {dayOrder.indexOf(parseInt(activeDayId.split('-')[1])) + 1}</div>
                          </div>
                        </div>
                        <div className="px-3 py-5 flex-1 flex items-center justify-center">
                          <div className="text-sm text-gray-500">Moving day...</div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}

            {viewType === 'daily' && (
              <div className={`space-y-4`}>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">
                    {visibleDayRange[0] === -1 ? "Day 0" : visibleDayRange[0] >= dayCount ? `Day ${dayCount + 1}` : `Day ${visibleDayRange[0] + 1}`}
                  </h2>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon" aria-label="Previous day" 
                      onClick={() => {
                        if (visibleDayRange[0] === 0) {
                          setVisibleDayRange([-1, 0]);
                        } else {
                          navigateDateRange('prev');
                        }
                      }} 
                      disabled={visibleDayRange[0] <= -1}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" aria-label="Next day" 
                      onClick={() => {
                        if (visibleDayRange[0] === dayCount - 1) {
                          setVisibleDayRange([dayCount, dayCount + 1]);
                        } else {
                          navigateDateRange('next');
                        }
                      }} 
                      disabled={visibleDayRange[0] >= dayCount}
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Show placeholder cards for day 0 or day after last */}
                {(visibleDayRange[0] === -1 || visibleDayRange[0] >= dayCount) ? (
                  <PlaceholderDayCardList 
                    position={visibleDayRange[0] === -1 ? "before" : "after"} 
                    dayNumber={visibleDayRange[0] === -1 ? "Day 0" : `Day ${dayCount + 1}`}
                  />
                ) : (
                  <>
                    {(filterComponents(groupedByDay[visibleDayRange[0]] || [])).length > 0 ? (
                      (filterComponents(groupedByDay[visibleDayRange[0]] || [])).map(c => (
                        <ItineraryItem key={c.id} component={c} onClick={() => {}} onDelete={() => handleDeleteComponent(c.id)} onEdit={() => {}} onRegenerate={() => {}} />
                      ))
                    ) : (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden bg-white dark:bg-gray-800">
                        <div className="bg-orange-600 text-white px-3 py-4 flex items-center justify-between">
                          <div className="font-semibold">Day {visibleDayRange[0] + 1}</div>
                          <div className="text-sm opacity-90">
                            {format(addDays(new Date(dateRange[0]), visibleDayRange[0]), 'EEE, MMM d')}
                          </div>
                        </div>
                        <div className="px-3 py-5">
                          <div className="text-center py-6">
                            <div className="text-sm text-gray-500 mb-3">No activities</div>
                            <button 
                              onClick={() => handleQuickAdd(visibleDayRange[0])}
                              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
                              title="Add component"
                            >
                              <PlusIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {viewType === 'list' && (
              <DndContext 
                sensors={sensors} 
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className={`space-y-6`}>
                  {/* Before placeholder */}
                  <PlaceholderDayCardList position="before" dayNumber="Day 0" />
                  
                  <SortableContext 
                    items={dayOrder.map(dayIndex => `day-${dayIndex}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {dayOrder.map((dayIndex, positionIndex) => {
                      const dayDate = addDays(new Date(dateRange[0]), dayIndex);
                      const comps = groupedByDay[dayIndex] || [];
                      
                      return (
                        <DraggableListDayCard
                          key={`day-${dayIndex}`}
                          dayIndex={dayIndex}
                          displayPosition={positionIndex}
                          dayDate={dayDate}
                          components={comps}
                        />
                      );
                    })}
                  </SortableContext>
                  
                  {/* After placeholder */}
                  <PlaceholderDayCardList position="after" dayNumber={`Day ${dayCount + 1}`} />
                </div>
                
                <DragOverlay>
                  {activeDayId ? (
                    <div className="opacity-95">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden bg-white dark:bg-gray-800 w-full">
                        <div className="bg-orange-600 text-white px-3 py-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4" />
                            <div className="font-semibold">Day {dayOrder.indexOf(parseInt(activeDayId.split('-')[1])) + 1}</div>
                          </div>
                        </div>
                        <div className="px-3 py-5 flex items-center justify-center">
                          <div className="text-sm text-gray-500">Moving day...</div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>

        {/* Footer command bar (uses fixed positioning internally). Leave space above via computed height. */}
        <div className="pointer-events-none">
          <div className="px-4 py-3 max-w-screen-2xl mx-auto flex items-center gap-2">
            <div className="flex-1 pointer-events-auto">
              <CommandBar 
                onSubmit={handleCommandSubmit}
                isProcessing={isProcessing}
                onDirectDataDisplay={() => {}}
                contactsData={contactsData}
                tripsData={tripsData}
                searchDataLoaded={searchDataLoaded}
                sidebarExpanded={!collapsed}
              />
            </div>
          </div>
        </div>

        {/* Modals */}
        <TripFormDialog 
          open={isEditTripModalOpen}
          onOpenChange={setIsEditTripModalOpen}
          trip={trip as any}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
            toast({ title: "Trip updated" });
          }}
        />
        <PdfImportDialog 
          tripId={tripId}
          open={isPdfImportOpen}
          onOpenChange={setIsPdfImportOpen}
          onImportComplete={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
            toast({ title: "PDF imported", description: "Components added from file" });
          }}
          trigger={<div />}
        />
        
        {/* Quick Add Modal */}
        <QuickAddModal 
          open={isQuickAddModalOpen}
          onOpenChange={setIsQuickAddModalOpen}
          onSelectType={handleSelectComponentType}
          dayIndex={selectedDayIndex}
        />
        
        {/* Add Component Modal */}
        {selectedComponentType && (
          <AddComponentModal 
            open={isAddComponentModalOpen}
            onOpenChange={setIsAddComponentModalOpen}
            tripId={tripId}
            type={selectedComponentType}
            dayIndex={selectedDayIndex}
            startDate={trip.startDate ? new Date(trip.startDate) : null}
            endDate={trip.endDate ? new Date(trip.endDate) : null}
            onSuccess={handleComponentCreated}
          />
        )}

        {/* Activity Stream (local slideout) */}
        {activityStreamOpen && (
          <>
            <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 shadow-xl border-l border-gray-200 dark:border-gray-700 z-40 transform transition-transform duration-300">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Activity Stream</h2>
                  <button
                    onClick={() => setActivityStreamOpen(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {activities.length === 0 ? (
                    <div className="text-sm text-gray-500">No recent activity.</div>
                  ) : (
                    activities.map((a) => (
                      <div key={a.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="text-sm font-medium text-gray-900">{a.title}</div>
                        <div className="text-xs text-gray-600 mt-1">{a.description}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div
              className="fixed inset-0 bg-black bg-opacity-25 z-30"
              onClick={() => setActivityStreamOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  </div>
  );
}


